<template>
  <div class="tech-multi-check">
    <kt-autoselect
      :label="props.label"
      :options="techOptions"
      :mode="props.mode"
      display-by="TechnicianName"
      value-by="Id"
      :multiple="true"
      v-model="model"
      search-placeholder="Search technicians..."
      :empty-text="props.emptyText"
      :on-refresh="handleRefresh"
      @change="handleChange"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, type PropType } from 'vue';

import KtAutoselect from '@/components/UI/KtAutoselect.vue';
import { useTechStore } from '@/stores/TechStore';
import type { TechnicianModel } from '@/models/TechnicianModel';

const props = defineProps({
  mode: {
    type: String,
    default: "dropdown"
  },
  label: { 
    type: String, 
    default: 'Select Technicians' 
  },
  filter: {
    type: Function as PropType<(tech: TechnicianModel) => boolean>,
    default: null
  },
  emptyText: {
    type: String,
    default: 'No Matching Technicians'
  }
});

const emit = defineEmits<{
  change: [value: number[]];
}>();

const model = defineModel<number[]>({
  default: () => []
});

const techStore = useTechStore();

// Raw list from the store. `techOptions` derives from it so the filter prop
// is re-applied reactively when the parent's available-id set (or any other
// reactive state the filter reads) changes after initial mount.
const rawTechs = ref<TechnicianModel[]>([]);

const techOptions = computed<TechnicianModel[]>(() =>
  props.filter ? rawTechs.value.filter(props.filter) : rawTechs.value
);

// Load technicians on mount
onMounted(async () => {
  const techs = await techStore.getActive();
  rawTechs.value = techs || [];
});

const handleChange = (value: number[]) => {
  emit('change', value);
};

const handleRefresh = async () => {
  await techStore.refresh();
  const techs = await techStore.getActive();
  rawTechs.value = techs || [];
};
</script>

